<template>
  <t-container class="t-text">
    <slot name="header">
      <h2 class="games-headline">{{ $t('tViews.joined.title_gamesSection') }}</h2>
    </slot>
    <div class="games-overview" purple>
      <div class="games-layout" data-qe-id="games-overview">
        <template v-for="(game, idx) in games" :key="idx">
          <t-tooltip
            class="games-slot-wrap tooltip-offset"
            :disabled="game.state !== 'pending'"
            :text="$t('gamesOverview.gameStates.pending_title')"
          >
            <t-card
              @click="onGame(game)"
              class="games-slot"
              :class="!isHistoryMode && idx === firstSlotOpen ? 'empty-open' : game.state"
              :data-qe-id="`game-slot-${game.state}`"
            >
              <div class="game-slot-content">
                <span class="game-number">
                  <span>{{ idx + 1 }}</span>
                </span>
                <div v-if="game.state === 'finished'" class="game-finished">
                  <span data-qe-id="score">{{ game.score }}</span>
                </div>
                <div v-else-if="game.state === 'pending'" class="game-pending">
                  <i class="icf-info"></i>
                  <c-horizontal-progress color="midGreen" />
                </div>
                <div v-else class="game-open"></div>
              </div>
            </t-card>
          </t-tooltip>
        </template>
      </div>
    </div>
  </t-container>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { GameUi } from '@/model/Tournament'
import GameState from '@/model/GameState'
const props = withDefaults(defineProps<{ isHistoryMode?: boolean; games: Array<GameUi> }>(), { isHistoryMode: false })
const emit = defineEmits(['onGame'])

const firstSlotOpen = computed(() => {
  const first = props.games.filter((game) => game.state === GameState.OPEN)[0]
  return first && first.idx
})

function onGame(game: GameUi) {
  if (game.state === GameState.FINISHED) {
    emit('onGame', game)
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import '~@npm/@gd/common-styles/dist/card';
@import '~@npm/@gd/common-styles/dist/color-vars';
@import '~@npm/@gd/common-styles/dist/icf-fonts';
@import '../styles/vars';

.games-headline {
  color: @c1-a;
  margin-top: 1em;
  text-shadow: 0 0.2rem 0 rgba(16, 49, 8, 0.4);
}
.games-layout {
  padding: 2em 0 0 0;
  display: flex;
  margin: auto;
  justify-content: center;
  flex-wrap: wrap;
  max-width: 61em;
  @media screen and (max-width: @framemaxwidth) {
    width: auto;
  }
}
.game-slot-content {
  position: relative;
  top: -2em;
}
.game-number {
  display: flex;
  font-weight: bold;
  justify-content: center;
  align-items: center;
  width: 3.8em;
  height: 3.8em;
  margin: 0 auto 0.5em;
  color: @c0;
  position: relative;
  // remove z-index: otherwise the tooltip for the disabled play button is behind the number batch
  // if z-index is required for other purposes please solve also the tooltip issue
  z-index: 2;
  filter: drop-shadow(0 0.1em 0.1em rgba(0, 0, 0, 0.3));
  &:before {
    content: '';
    z-index: -1;
    background: url(../assets/game-overview/slot_badge.svg) no-repeat;
    width: 3.8em;
    height: 3.8em;
    position: absolute;
    top: 0;
    left: 0;
  }
  & > span {
    font-size: 2em;
  }
}
.colorScheme.red & {
  .game-number:before {
    background: url(../assets/game-overview/slot_badge_romme.svg) no-repeat;
  }
}
.games-slot-wrap {
  margin: calc(@biggerspacing / 2) calc(@biggerspacing / 2) @biggerspacing;
}
.tooltip-offset::v-deep .t-tooltip {
  // offset to show tooltip above the number batch
  bottom: 2em;
}
.games-slot {
  width: 7em;
  height: 5.2em;
  background: white;
  background-color: white;
  box-shadow: 0 0 0 1px #d9e2e7 inset, 0 0.3em 0 #6a7378, 0 0 3em rgba(49, 58, 63, 0.1);
  &.finished {
    cursor: pointer;
    .colorScheme.midGreen & {
      .card(@colorScheme--midGreen);
      .card-select(@colorScheme--midGreen);
    }
    .colorScheme.red & {
      .card(@colorScheme--red);
      .card-select(@colorScheme--red);
    }
  }
  &.empty-open {
    .colorScheme.midGreen & {
      .card(@colorScheme--midGreen);
    }
    .colorScheme.red & {
      .card(@colorScheme--red);
    }
  }
  &.open {
    .card(@c0-h);
    background: @c0-c;
    .game-number {
      color: @c0-b;
      &:before {
        background: url(../assets/game-overview/slot_badge_inactive.svg) no-repeat;
      }
    }
  }
  &.pending {
    .colorScheme.midGreen & {
      .card(@colorScheme--midGreen);
    }
    .colorScheme.red & {
      .card(@colorScheme--red);
    }
  }
  &.lto {
    .card(@c8-e);
  }
  .game-finished {
    & > span {
      font-weight: bold;
      font-size: 1.25em;
      line-height: 1.25em;
    }
  }
  .game-pending {
    display: flex;
    align-items: center;
    justify-content: space-around;
    i {
      font-size: 1.4em;
      color: @c9-g;
    }
  }
  .game-open {
  }
  .game-load-timeout {
  }
}
</style>
